import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet } from '../ui/Sheet.jsx';
import { useUi } from '../../stores/ui.js';
import { useDebounced } from '../../hooks/useDebounced.js';
import { useSearchSuggestions } from '../../features/catalog/queries.js';
import { imageProps, formatMoney, formatPriceRange } from '../../lib/format.js';
import { Spinner } from '../ui/Spinner.jsx';

/**
 * Search, as a sheet rather than a page.
 *
 * Suggestions are debounced and only fire from two characters, because a request
 * per keystroke sends twenty to answer one question. Submitting goes to the full
 * results page, which is a real URL somebody can share.
 */

export const SearchOverlay = () => {
  const open = useUi((state) => state.searchOpen);
  const close = useUi((state) => state.closeSearch);
  const navigate = useNavigate();

  const [term, setTerm] = useState('');
  const debounced = useDebounced(term, 250);
  const { data: suggestions, isFetching } = useSearchSuggestions(debounced);

  useEffect(() => {
    if (!open) setTerm('');
  }, [open]);

  const go = (path) => {
    close();
    navigate(path);
  };

  const submit = (event) => {
    event.preventDefault();
    if (term.trim().length < 2) return;
    go(`/search?search=${encodeURIComponent(term.trim())}`);
  };

  const hasResults =
    (suggestions?.products?.length ?? 0) +
      (suggestions?.brands?.length ?? 0) +
      (suggestions?.categories?.length ?? 0) >
    0;

  return (
    <Sheet open={open} onClose={close} side="bottom" title="Search">
      <div className="px-5 py-4">
        <form onSubmit={submit} role="search">
          <label htmlFor="site-search" className="sr-only">
            Search products, brands, and categories
          </label>
          <div className="relative">
            <input
              id="site-search"
              type="search"
              autoFocus
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder="Amber, kettlebell, 100 ml…"
              autoComplete="off"
              className="h-13 w-full rounded border border-mist bg-surface pr-12 pl-4 text-base text-ink placeholder:text-muted/70 focus:outline-2 focus:outline-offset-1 focus:outline-accent"
            />
            {isFetching && (
              <Spinner className="absolute top-1/2 right-4 size-4 -translate-y-1/2 text-muted" />
            )}
          </div>
        </form>

        <div className="mt-5 max-h-[52vh] overflow-y-auto" aria-live="polite">
          {debounced.trim().length < 2 ? (
            <p className="py-6 text-center text-xs text-muted">
              Type at least two characters. Try a note, a material, or a size.
            </p>
          ) : !hasResults && !isFetching ? (
            <p className="py-6 text-center text-xs text-muted">
              Nothing matches “{debounced}”. The full catalogue is under{' '}
              <button type="button" onClick={() => go('/shop')} className="text-ink underline underline-offset-4">
                Everything
              </button>
              .
            </p>
          ) : (
            <div className="space-y-6">
              {suggestions?.products?.length > 0 && (
                <section>
                  <h3 className="font-mono text-2xs tracking-[0.14em] text-muted uppercase">Products</h3>
                  <ul className="mt-2.5 divide-y divide-mist">
                    {suggestions.products.map((product) => (
                      <li key={product._id}>
                        <button
                          type="button"
                          onClick={() => go(`/product/${product.slug}`)}
                          className="flex w-full items-center gap-3 py-2.5 text-left hover:bg-paper focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
                        >
                          {product.images?.[0] && (
                            <img
                              {...imageProps(product.images[0], { sizes: '48px' })}
                              alt=""
                              className="size-12 shrink-0 border border-mist object-cover"
                            />
                          )}
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm text-ink">{product.title}</span>
                            <span className="font-mono text-2xs text-muted" data-numeric>
                              {product.type === 'variable'
                                ? formatPriceRange(product.priceRange, product.price)
                                : formatMoney(product.price)}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {suggestions?.categories?.length > 0 && (
                <SuggestionGroup
                  title="Categories"
                  items={suggestions.categories}
                  onPick={(item) => go(`/category/${item.slug}`)}
                />
              )}

              {suggestions?.brands?.length > 0 && (
                <SuggestionGroup
                  title="Brands"
                  items={suggestions.brands}
                  onPick={(item) => go(`/brand/${item.slug}`)}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </Sheet>
  );
};

const SuggestionGroup = ({ title, items, onPick }) => (
  <section>
    <h3 className="font-mono text-2xs tracking-[0.14em] text-muted uppercase">{title}</h3>
    <ul className="mt-2 flex flex-wrap gap-2">
      {items.map((item) => (
        <li key={item._id}>
          <button
            type="button"
            onClick={() => onPick(item)}
            className="rounded border border-mist px-3 py-1.5 text-xs text-ink transition-colors hover:border-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            {item.name}
          </button>
        </li>
      ))}
    </ul>
  </section>
);

export default SearchOverlay;
